import { useEffect, useState } from 'react';
import { useUserAuth } from './useUserAuth';
import { LOGGED_IN_STATE } from 'lib/config/appConstants';

export type LoggedInState = (typeof LOGGED_IN_STATE)[keyof typeof LOGGED_IN_STATE];

/**
 * Custom hook to initialize authentication state and determine the logged-in status.
 *
 * @param isReady - A boolean indicating whether the application is ready to perform authentication checks. Defaults to `false`.
 * @returns The current logged-in state, represented as a `LoggedInState` enum.
 *
 * This hook performs the following:
 * - Initializes the logged-in state to `LOGGED_IN_STATE.UNKNOWN`.
 * - Fetches user authentication data using the `useUserAuth` hook.
 * - Updates the logged-in state to `LOGGED_IN_STATE.LOGGED_IN` when the user authentication query is successful.
 *
 */
export function useAuthInitialization(isReady = false): LoggedInState {
  const [loggedInState, setLoggedInState] = useState<LoggedInState>(LOGGED_IN_STATE.UNKNOWN);

  const userQuery = useUserAuth(isReady);

  useEffect(() => {
    // Set logged-in state as soon as user authentication is successful
    if (userQuery.isSuccess) {
      setLoggedInState(LOGGED_IN_STATE.LOGGED_IN);
    }
  }, [userQuery.isSuccess]);

  return loggedInState;
}
